import * as z from 'zod';

export const schema = z.object({
  postName: z.string().min(1, 'Post is required'),

  category: z.object({
    categoryName: z.string().min(1, "Label is required"),
    categoryGuid: z.string().min(1, "Value is required"),
  }, { invalid_type_error: 'Category is required' }),

  isRegister: z.object({
    label: z.string().min(1, "Label is required"),
    value: z.boolean(),
  }, { invalid_type_error: 'Registration Required is required' }),

  postDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Post Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),

  // maxApplication: z.union([
  //   z.string().min(0, "Max Application is required").regex(/^\d+(\.\d+)?$/, "Max Application must be a valid number"),
  //   z.number().positive("Max Application must be a positive number"),
  // ]).nullable().optional(),

  // maxApplication: z.union([z.string().optional(), z.number().optional()]).refine(
  //   (value) => value === "" || value === undefined || parseFloat(value) > 0,
  //   { message: "Max Application must be a positive number" }
  // ).optional().nullable(),
  maxApplication: z
  .union([z.string().optional(), z.number().optional()])
  .refine(
    (value) =>
      value === "" ||
      value === undefined ||
      (typeof value === "string" && /^\d+$/.test(value)) || // string of digits only
      (typeof value === "number" && Number.isInteger(value) && value > 0),
    { message: "Max Application must be a positive whole number" }
  )
  .optional()
  .nullable(),

  form: z.object({
    formName: z.string().min(1, "Label is required"),
    formGuid: z.string().min(1, "Value is required"),
  }, { invalid_type_error: 'Form is required' }).optional().nullable(),

  titleCnsimp: z.string().min(1, '标题 is required'),
  titleCntrad: z.string().min(1, '标题 is required'),
  titleEng: z.string().min(1, 'Title is required'),

  contentCnsimp: z.string().min(1, '内容 is required'),
  contentCntrad: z.string().min(1, '內容 is required'),
  contentEng: z.string().min(1, 'Content is required'),

  applyStartDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Apply Start Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }).optional().nullable(),

  applyCloseDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Apply Close Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }).optional().nullable(),

  maxApplyQty: z
    .union([z.string().optional(), z.number().optional()])
    .refine(
      (value) =>
        value === "" ||
        value === undefined ||
        (typeof value === "string" && /^\d+$/.test(value) && parseInt(value, 10) > 10) ||
        (typeof value === "number" && Number.isInteger(value) && value > 10),
      { message: "Max Apply Quantity must be empty or a whole number greater than 10" }
    )
    .optional()
    .nullable(),

  maxAcceptQty: z
    .union([z.string().optional(), z.number().optional()])
    .refine(
      (value) =>
        value === "" ||
        value === undefined ||
        (typeof value === "string" && /^\d+$/.test(value) && parseInt(value, 10) > 5) ||
        (typeof value === "number" && Number.isInteger(value) && value > 5),
      { message: "Max Accept Quantity must be empty or a whole number greater than 5" }
    )
    .optional()
    .nullable(),

  incharge1: z.object({
    fullName: z.string().min(1, "Label is required"),
    userGuid: z.string().min(1, "Value is required"),
  }, { invalid_type_error: 'In Charge 1 is required' }),

  incharge2: z.object({
    fullName: z.string().min(1, "Label is required"),
    userGuid: z.string().min(1, "Value is required"),
  }, { invalid_type_error: 'In Charge 2 is required' }),

  eventStartDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Event Start Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),

  eventEndDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Event End Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),

  paymentTerm: z.object({
    label: z.string().min(1, "Label is required"),
    value: z.number().min(1, "Value is required"),
  }, { invalid_type_error: 'Payment Term is required' }).optional().nullable(),

  postFeeOption: z.array(
    z.object({
      optionNameEng: z.string().min(1, 'Option is required'),
      optionNameCntrad: z.string().min(1, '選項 is required'),
      optionNameCnsimp: z.string().min(1, '选项 is required'),
      fee: z
        .union([z.string(), z.number()])
        .refine(value => {
          const numValue = typeof value === 'string' ? parseFloat(value) : value;
          return numValue >= 2;
        }, {
          message: 'Fee must be at least 2',
        }),
      sequence: z.union([
        z.string().optional().nullable(),
        z.number().optional().nullable(),
      ]),
    })
  ).min(0, { message: 'Option is required' }),

  /* view only */
  postNumber: z.string().optional(),
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
  voidBy: z.string().optional(),
  voidDate: z.string().optional(),
  voidRemark: z.string().optional(),
}).superRefine((data, ctx) => {
  // Apply Start Date cannot be earlier than Post Date
  if (data.applyStartDate && data.applyStartDate < data.postDate) {
    ctx.addIssue({
      path: ["applyStartDate"],
      message: "Apply Start Date cannot be earlier than Post Date",
    });
  }

  // Apply Close Date cannot be earlier than Apply Start Date
  if (data.applyCloseDate && data.applyCloseDate < data.applyStartDate) {
    ctx.addIssue({
      path: ["applyCloseDate"],
      message: "Apply Close Date cannot be earlier than Apply Start Date",
    });
  }

  // Event Start Date cannot be earlier than Post Date
  if (data.eventStartDate < data.postDate) {
    ctx.addIssue({
      path: ["eventStartDate"],
      message: "Event Start Date cannot be earlier than Post Date",
    });
  }

  // Event Start Date cannot be earlier than Apply Close Date
  if (data.eventStartDate < data.applyCloseDate) {
    ctx.addIssue({
      path: ["eventStartDate"],
      message: "Event Start Date cannot be earlier than Apply Close Date",
    });
  }

  // Event Close Date cannot be earlier than Event Start Date
  if (data.eventEndDate < data.eventStartDate) {
    ctx.addIssue({
      path: ["eventEndDate"],
      message: "Event End Date cannot be earlier than Event Start Date",
    });
  }

  // InCharge1 and incharge2 cannot be the same
  if (data.incharge1.userGuid === data.incharge2.userGuid) {
    ctx.addIssue({
      path: ["incharge2"],
      message: "In Charge 2 cannot be the same as In Charge 1",
    });
  }

  // Conditional validation based on isRegister
  if (data.isRegister.value) {
    if (!data.maxApplication) {
      ctx.addIssue({
        path: ["maxApplication"],
        message: "Max Application is required",
      });
    }

    if (!data.applyStartDate) {
      ctx.addIssue({
        path: ["applyStartDate"],
        message: "Apply Start Date is required",
      });
    }

    if (!data.applyCloseDate) {
      ctx.addIssue({
        path: ["applyCloseDate"],
        message: "Apply Close Date is required",
      });
    }

    if (!data.maxApplyQty) {
      ctx.addIssue({
        path: ["maxApplyQty"],
        message: "Max Apply Quantity is required",
      });
    }

    if (!data.maxAcceptQty) {
      ctx.addIssue({
        path: ["maxAcceptQty"],
        message: "Max Accept Quantity is required",
      });
    }

    if (!data.form) {
      ctx.addIssue({
        path: ["form"],
        message: "Form is required",
      });
    }

    if (!data.paymentTerm) {
      ctx.addIssue({
        path: ["paymentTerm"],
        message: "Payment Term is required",
      });
    }

    if (!data.postFeeOption) {
      ctx.addIssue({
        path: ["postFeeOption"],
        message: "Post Fee Option is required",
      });
    }
  }
});

export const baseSchema = {
  postName: z.string().min(1, 'Post is required'),

  category: z.object({
    categoryName: z.string().min(1, "Label is required"),
    categoryGuid: z.string().min(1, "Value is required"),
  }, { invalid_type_error: 'Category is required' }),

  isRegister: z.object({
    label: z.string().min(1, "Label is required"),
    value: z.boolean(),
  }, { invalid_type_error: 'Registration Required is required' }),

  postDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Post Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),

  titleCnsimp: z.string().min(1, '标题 is required'),
  titleCntrad: z.string().min(1, '标题 is required'),
  titleEng: z.string().min(1, 'Title is required'),

  contentCnsimp: z.string().min(1, '内容 is required'),
  contentCntrad: z.string().min(1, '內容 is required'),
  contentEng: z.string().min(1, 'Content is required'),

  incharge1: z.object({
    fullName: z.string().min(1, "Label is required"),
    userGuid: z.string().min(1, "Value is required"),
  }, { invalid_type_error: 'In Charge 1 is required' }),

  incharge2: z.object({
    fullName: z.string().min(1, "Label is required"),
    userGuid: z.string().min(1, "Value is required"),
  }, { invalid_type_error: 'In Charge 2 is required' }),

  eventStartDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Event Start Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),

  eventEndDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Event End Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),

  /* View only fields */
  postNumber: z.string().optional(),
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
  voidBy: z.string().optional(),
  voidDate: z.string().optional(),
  voidRemark: z.string().optional(),
};

export const registerSchema = {
  maxApplication: z.union([
    z.string().min(1, "Max Application is required").regex(/^\d+(\.\d+)?$/, "Max Application must be a valid number"),
    z.number().positive("Max Application must be a positive number"),
  ]),

  applyStartDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Apply Start Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),

  applyCloseDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({ invalid_type_error: 'Apply Close Date is required' }))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),

  maxApplyQty: z.union([z.string().optional(), z.number().optional()])
    .refine((value) => value === "" || value === undefined || parseFloat(value) > 10, {
      message: "Max Apply Quantity must be empty or greater than 10",
    }),

  maxAcceptQty: z.union([z.string().optional(), z.number().optional()])
    .refine((value) => value === "" || value === undefined || parseFloat(value) > 5, {
      message: "Max Accept Quantity must be empty or greater than 5",
    }),

  form: z.object({
    formName: z.string().min(1, "Label is required"),
    formGuid: z.string().min(1, "Value is required"),
  }, { invalid_type_error: 'Form is required' }),

  paymentTerm: z.object({
    label: z.string().min(1, "Label is required"),
    value: z.number().min(1, "Value is required"),
  }, { invalid_type_error: 'Payment Term is required' }),

  postFeeOption: z.array(
    z.object({
      optionNameEng: z.string().min(1, 'Option is required'),
      optionNameCntrad: z.string().min(1, '選項 is required'),
      optionNameCnsimp: z.string().min(1, '选项 is required'),
      fee: z.union([z.string(), z.number()])
        .refine(value => {
          const numValue = typeof value === 'string' ? parseFloat(value) : value;
          return numValue >= 2;
        }, {
          message: 'Fee must be at least 2',
        }),
      sequence: z.union([
        z.string().optional().nullable(),
        z.number().optional().nullable(),
      ]),
    })
  ).min(1, { message: 'Option is required' }),
};


// const schema = z.object({
//   postName: z.string().min(1, 'Post is required'),

//   category: z.object({
//     categoryName: z.string().min(1, "Label is required"),
//     categoryGuid: z.string().min(1, "Value is required"),
//   }, {invalid_type_error: 'Category is required'}),

//   isRegister: z.object({
//     label: z.string().min(1, "Label is required"),
//     value: z.boolean(),
//   }, {invalid_type_error: 'Registration Required is required'}),

//   postDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({invalid_type_error: 'Post Date is required'}))
//   .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),
//   maxApplication: z.union([
//     z.string().min(1, "Max Application is required").regex(/^\d+(\.\d+)?$/, "Max Application must be a valid number"),
//     z.number().positive("Max Application must be a positive number"),
//   ]),
//   form: z.object({
//     formName: z.string().min(1, "Label is required"),
//     formGuid: z.string().min(1, "Value is required"),
//   }, {invalid_type_error: 'Form is required'}),

//   titleCnsimp: z.string().min(1, '标题 is required'),
//   titleCntrad: z.string().min(1, '标题 is required'),
//   titleEng: z.string().min(1, 'Title is required'),

//   contentCnsimp: z.string().min(1, '内容 is required'),
//   contentCntrad: z.string().min(1, '內容 is required'),
//   contentEng: z.string().min(1, 'Content is required'),
  

//   applyStartDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({invalid_type_error: 'Apply Start Date is required'}))
//   .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),
//   applyCloseDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({invalid_type_error: 'Apply Close Date is required'}))
//   .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),
//   maxApplyQty: z.union([ z.string().optional(), z.number().optional() ]).refine(
//     (value) => value === "" || value === undefined || parseFloat(value) > 10,
//     { message: "Max Apply Quantity must be empty or greater than 10" }
//   ),
//   maxAcceptQty: z.union([ z.string().optional(), z.number().optional() ]).refine(
//     (value) => value === "" || value === undefined || parseFloat(value) > 5,
//     { message: "Max Accept Quantity must be empty or greater than 5" }
//   ),
//   incharge1: z.object({
//     fullName: z.string().min(1, "Label is required"),
//     userGuid: z.string().min(1, "Value is required"),
//   }, {invalid_type_error: 'In Charge 1 is required'}),
//   incharge2: z.object({
//     fullName: z.string().min(1, "Label is required"),
//     userGuid: z.string().min(1, "Value is required"),
//   }, {invalid_type_error: 'In Charge 2 is required'}),

//   eventStartDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({invalid_type_error: 'Event Start Date is required'}))
//   .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),
//   eventEndDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({invalid_type_error: 'Event End Date is required'}))
//   .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),

//   paymentTerm: z.object({
//     label: z.string().min(1, "Label is required"),
//     value: z.number().min(1, "Value is required"),
//   }, {invalid_type_error: 'Payment Term is required'}),

//   postFeeOption: z.array(
//     z.object({
//       optionNameEng: z.string().min(1, 'Option is required'),
//       optionNameCntrad: z.string().min(1, '選項 is required'),
//       optionNameCnsimp: z.string().min(1, '选项 is required'),
//       fee: z
//       .union([z.string(), z.number()])
//       .refine(value => {
//         const numValue = typeof value === 'string' ? parseFloat(value) : value;
//         return numValue >= 2;
//       }, {
//         message: 'Fee must be at least 2',
//       }),
//       sequence: z.union([
//         z.string().optional().nullable(),
//         z.number().optional().nullable(),
//       ]),
//     })
//   ).min(1, { message: 'Option is required' }),

//   /* view only */
//   postNumber: z.string().optional(),
//   createdOn: z.string().optional(),
//   createdBy: z.string().optional(),
//   voidBy: z.string().optional(),
//   voidDate: z.string().optional(),
//   voidRemark: z.string().optional(),
// }).superRefine((data, ctx) => {
//   // Apply Start Date cannot be earlier than Post Date
//   if (data.applyStartDate < data.postDate) {
//     ctx.addIssue({
//       path: ["applyStartDate"],
//       message: "Apply Start Date cannot be earlier than Post Date",
//     });
//   }

//   // Apply Close Date cannot be earlier than Apply Start Date
//   if (data.applyCloseDate < data.applyStartDate) {
//     ctx.addIssue({
//       path: ["applyCloseDate"],
//       message: "Apply Close Date cannot be earlier than Apply Start Date",
//     });
//   }

//   // Event Start Date cannot be earlier than Post Date
//   if (data.eventStartDate < data.postDate) {
//     ctx.addIssue({
//       path: ["eventStartDate"],
//       message: "Event Start Date cannot be earlier than Post Date",
//     });
//   }

//   // Event Start Date cannot be earlier than Apply Close Date
//   if (data.eventStartDate < data.applyCloseDate) {
//     ctx.addIssue({
//       path: ["eventStartDate"],
//       message: "Event Start Date cannot be earlier than Apply Close Date",
//     });
//   }

//   // Event Close Date cannot be earlier than Event Start Date
//   if (data.eventEndDate < data.eventStartDate) {
//     ctx.addIssue({
//       path: ["eventEndDate"],
//       message: "Event End Date cannot be earlier than Event Start Date",
//     });
//   }

//   // InCharge1 and incharge2 cannot be the same
//   if (data.incharge1.userGuid === data.incharge2.userGuid) {
//     ctx.addIssue({
//       path: ["incharge2"],
//       message: "In Charge 2 cannot be the same as In Charge 1",
//     });
//   }
// });